import React from "react";
import moment from "moment";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import ImageIcon from "@mui/icons-material/Image";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CampaignPageService from "../../services/CampaignPageService";
import { CampaignPageServiceEnum, CreateCampaignPage } from "../../interfaces/CampaignPage/CampaignPage";

const campaignPageService = new CampaignPageService();

export type CampaignPageFormValue = CreateCampaignPage;

/** Same rule the backend enforces: lowercase letters and digits joined by single hyphens. */
export const CAMPAIGN_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const CAMPAIGN_SITE_URL = "https://thechefkart.com/campaign";

const serviceOptions = [
    { label: "Chefit (one-time cook)", value: CampaignPageServiceEnum.CHEFIT },
    { label: "Party (chef for party)", value: CampaignPageServiceEnum.PARTY },
];

/** The first missing or invalid field, as a message for the snackbar; null when the form can be sent. */
export const validateCampaignPageForm = (form: CampaignPageFormValue): string | null => {
    if (!CAMPAIGN_SLUG_PATTERN.test(form.slug)) return "Slug must be lowercase letters and digits joined by hyphens, e.g. diwali-2026";
    if (!form.campaignName.trim()) return "Campaign name is required";
    if (!form.service) return "Service is required";
    if (!form.metaTitle.trim()) return "Meta title is required";
    if (!form.metaDescription.trim()) return "Meta description is required";
    if (!form.bannerHeading.trim()) return "Banner heading is required";
    if (!form.bannerDescription.trim()) return "Banner description is required";
    if (!/^https:\/\//.test(form.bannerImageUrl.trim())) return "Upload a banner image";
    return null;
};

type Props = {
    mode: "create" | "edit";
    value: CampaignPageFormValue;
    onChange: (key: keyof CampaignPageFormValue, value: any) => void;
    onSubmit: () => void;
    onCancel: () => void;
    submitting?: boolean;
};

const SectionCard = ({
    icon,
    title,
    subtitle,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            border: "1px solid #e8ebed",
            borderRadius: 2,
            mb: 2,
        }}
    >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: subtitle ? 0.5 : 2 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
        </Stack>
        {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {subtitle}
            </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
        {children}
    </Paper>
);

const BannerUploader = ({
    slug,
    imageUrl,
    onUploaded,
}: {
    slug: string;
    imageUrl: string;
    onUploaded: (url: string) => void;
}) => {
    const [uploading, setUploading] = React.useState(false);

    const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const extension = file.name.split(".").pop();
        const timestamp = moment().format("DDMMYYYYHHmmss");
        const fileName = `${slug || "campaign"}_${timestamp}.${extension}`;

        setUploading(true);
        try {
            const links = await campaignPageService.getBannerUploadLinks(fileName);
            await campaignPageService.uploadBanner(links?.putUrl, file);
            onUploaded(links?.getUrl);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Banner uploaded — save to publish it",
                type: CustomSnackbarTypes.SUCCESS,
            });
        } catch (err) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Failed to upload banner",
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Stack gap={1.5}>
            <Box>
                <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}>
                    {imageUrl ? "Replace banner" : "Upload banner"}
                    <input hidden accept="image/png,image/jpeg,image/webp" type="file" onChange={onImageSelect} />
                </Button>
                {uploading && <CircularProgress size={20} sx={{ ml: 2, verticalAlign: "middle" }} />}
            </Box>
            <Typography variant="caption" color="text.secondary">
                Shown full-width at the top of the page on desktop and mobile. The current banners are about 2:1 (e.g. 1454×726).
            </Typography>
            {imageUrl && (
                <Box
                    component="img"
                    src={imageUrl}
                    alt="Banner preview"
                    sx={{
                        width: "100%",
                        maxWidth: 640,
                        borderRadius: "8px",
                        border: "1px solid rgba(0,0,0,0.1)",
                    }}
                />
            )}
        </Stack>
    );
};

const CampaignPageFormFields: React.FC<Props> = ({
    mode,
    value,
    onChange,
    onSubmit,
    onCancel,
    submitting,
}) => {
    const slugInvalid = value.slug.length > 0 && !CAMPAIGN_SLUG_PATTERN.test(value.slug);

    return (
        <Box sx={{ pb: 10 }}>
            <SectionCard icon={<CampaignIcon color="primary" />} title="Campaign">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Campaign Name"
                            helperText="For Centaurus only; not shown on the page"
                            fullWidth
                            required
                            value={value.campaignName}
                            onChange={(e) => onChange("campaignName", e.target.value)}
                            size="small"
                            inputProps={{ maxLength: 100 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Slug"
                            fullWidth
                            required
                            disabled={mode === "edit"}
                            error={slugInvalid}
                            helperText={
                                mode === "edit"
                                    ? `${CAMPAIGN_SITE_URL}/${value.slug} — the URL cannot be changed`
                                    : slugInvalid
                                        ? "Lowercase letters and digits joined by hyphens, e.g. diwali-2026"
                                        : `${CAMPAIGN_SITE_URL}/${value.slug || "…"}`
                            }
                            value={value.slug}
                            onChange={(e) => onChange("slug", e.target.value.trim().toLowerCase())}
                            size="small"
                            inputProps={{ maxLength: 80 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Service</InputLabel>
                            <Select
                                label="Service"
                                value={value.service ?? ""}
                                onChange={(e) => onChange("service", e.target.value)}
                            >
                                {serviceOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary">
                            Decides the booking widget and where the banner's button leads.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={value.isActive}
                                    onChange={(e) => onChange("isActive", e.target.checked)}
                                />
                            }
                            label="Active (an inactive page returns 404)"
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard
                icon={<ImageIcon color="primary" />}
                title="Banner"
                subtitle="The only part of the page that differs between campaigns. Everything below it is shared."
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Heading"
                            fullWidth
                            required
                            value={value.bannerHeading}
                            onChange={(e) => onChange("bannerHeading", e.target.value)}
                            size="small"
                            inputProps={{ maxLength: 255 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Description"
                            fullWidth
                            required
                            value={value.bannerDescription}
                            onChange={(e) => onChange("bannerDescription", e.target.value)}
                            size="small"
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <BannerUploader
                            slug={value.slug}
                            imageUrl={value.bannerImageUrl}
                            onUploaded={(url) => onChange("bannerImageUrl", url)}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard icon={<TravelExploreIcon color="primary" />} title="SEO">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Meta Title"
                            helperText="The browser tab and search result title"
                            fullWidth
                            required
                            value={value.metaTitle}
                            onChange={(e) => onChange("metaTitle", e.target.value)}
                            size="small"
                            inputProps={{ maxLength: 255 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Meta Description"
                            fullWidth
                            required
                            multiline
                            minRows={2}
                            value={value.metaDescription}
                            onChange={(e) => onChange("metaDescription", e.target.value)}
                            size="small"
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={value.noindex}
                                    onChange={(e) => onChange("noindex", e.target.checked)}
                                />
                            }
                            label="Hide from search engines (noindex)"
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The website picks up changes within about 10 minutes.
            </Typography>

            <Paper
                elevation={3}
                sx={{
                    position: "sticky",
                    bottom: 0,
                    mt: 2,
                    p: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #e8ebed",
                    zIndex: 5,
                }}
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={onSubmit}
                    disabled={submitting}
                >
                    {submitting ? "Saving..." : mode === "create" ? "Create" : "Save Changes"}
                </Button>
            </Paper>
        </Box>
    );
};

export default CampaignPageFormFields;
